import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
// We still need the icons
import { FaTools, FaClock, FaCheckCircle, FaPlus, FaTrash, FaTimes } from 'react-icons/fa';
// --- THIS IS THE CORRECTED LINK ---
// We use ../ to go "up" one folder from /pages to /src
import '../index.css';

// Removed mockScheduleData - Upcoming Schedule section removed

// This is the main component for the Maintenance Officer
export default function Maintenance() {
  const { user } = useAuth();
  // --- State Variables ---
  const [workOrders, setWorkOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [newPriority, setNewPriority] = useState('Medium');
  const [newAssigned, setNewAssigned] = useState('');
  const [newEventId, setNewEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch work orders and events from backend
  useEffect(() => {
    fetchWorkOrders();
    fetchEvents();
  }, []);

  async function fetchEvents() {
    try {
      const response = await api.get('/events');
      setEvents(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load events:', err);
    }
  }

  async function fetchWorkOrders() {
    setLoading(true);
    try {
      const response = await api.get('/maintenance');
      setWorkOrders(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load work orders:', err);
      setError('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }


  // --- Event Handlers (Backend Connected) ---

  // Function to handle creating a new task
  async function handleCreateTask(e) {
    e.preventDefault();
    if (!newTask) return; // Don't create empty tasks

    try {
      // Send assignedTo - convert empty string to null
      const assignedValue = newAssigned.trim() === '' ? null : newAssigned.trim();
      
      const response = await api.post('/maintenance', {
        eventId: newEventId || null,
        issue: newTask,
        priority: newPriority,
        assignedTo: assignedValue
      });
      
      // Immediately add the new work order to the list for instant UI update
      if (response.data) {
        setWorkOrders(prev => [response.data, ...prev]);
      }
      
      // Also refresh work orders to get the complete data from server
      await fetchWorkOrders();
      setNewTask('');
      setNewPriority('Medium');
      setNewAssigned('');
      setNewEventId('');
      setShowModal(false);
    } catch (err) {
      alert('Failed to create work order: ' + (err?.response?.data?.message || err?.message));
    }
  }

  // Function to mark a task as "Complete"
  async function handleCompleteTask(taskId) {
    try {
      const response = await api.put(`/maintenance/${taskId}`, {
        status: 'Completed'
      });
      
      // Refresh work orders from server to get updated data
      await fetchWorkOrders();
    } catch (err) {
      alert('Failed to update work order: ' + (err?.response?.data?.message || err?.message));
      // Refresh to show current state
      fetchWorkOrders();
    }
  }
  
  // Function to delete a task
  async function handleDeleteTask(taskId) {
    if (!window.confirm('Are you sure you want to delete this work order?')) {
      return;
    }
    
    try {
      await api.delete(`/maintenance/${taskId}`);
      
      // Refresh work orders from server to get updated list
      await fetchWorkOrders();
    } catch (err) {
      alert('Failed to delete work order: ' + (err?.response?.data?.message || err?.message));
      // Refresh to show current state
      fetchWorkOrders();
    }
  }

  // Function to update assigned person
  async function handleUpdateAssigned(taskId, newAssigned) {
    try {
      // Always send assignedTo explicitly, even if it's null or empty
      // Convert empty string to null for database
      const assignedValue = (newAssigned === '' || newAssigned === 'Unassigned' || newAssigned === null || newAssigned === undefined) ? null : newAssigned;
      
      // Make sure we're sending it as a string or null, not undefined
      const response = await api.put(`/maintenance/${taskId}`, {
        assignedTo: assignedValue === null ? null : String(assignedValue)
      });
      
      // Refresh work orders to get updated data from server
      await fetchWorkOrders();
    } catch (err) {
      alert('Failed to update assigned person: ' + (err?.response?.data?.message || err?.message));
      // Refresh to revert any local changes
      fetchWorkOrders();
    }
  }
  
  // Helper function to get class names for priority
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'High': return 'priority-high';
      case 'Medium': return 'priority-medium';
      default: return 'priority-low';
    }
  };

  // Helper function for status class names
  const getStatusClass = (status) => {
    switch (status) {
      case 'New': return 'status-new';
      case 'In Progress': return 'status-progress';
      case 'Completed': return 'status-completed';
      default: return 'status-low';
    }
  };


  // --- JSX (The HTML part) ---
  return (
    // Use the .dashboard-page style from your CSS
    <div className="p-6 dashboard-page">
      
      {/* --- Header and "Create New" Button --- */}
      <div className="flex-header mb-4">
        <h2 className="hero-title">Maintenance Dashboard</h2>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary" // Use .btn and .btn-primary from your CSS
        >
          <FaPlus style={{ marginRight: '8px' }} /> Create New Work Order
        </button>
      </div>

      {/* --- KPI Cards (Colorful) --- */}
      <div className="stats-grid mb-4">
        {/* I created a new .kpi-card style for these */}
        <div className="kpi-card kpi-card-blue">
          <FaTools className="kpi-icon" />
          <div>
            <h3 className="kpi-title">New Requests</h3>
            <p className="kpi-value">
              {workOrders.filter(t => t.status === 'New' || t.status === 'Pending').length}
            </p>
          </div>
        </div>
        <div className="kpi-card kpi-card-yellow">
          <FaClock className="kpi-icon" />
          <div>
            <h3 className="kpi-title">Tasks In Progress</h3>
            <p className="kpi-value">
              {workOrders.filter(t => t.status === 'Pending').length}
            </p>
          </div>
        </div>
        <div className="kpi-card kpi-card-green">
          <FaCheckCircle className="kpi-icon" />
          <div>
            <h3 className="kpi-title">Tasks Completed</h3>
            <p className="kpi-value">
              {workOrders.filter(t => 
                t.status === 'Completed' || t.status === 'Resolved'
              ).length}
            </p>
          </div>
        </div>
      </div>

      {/* --- Main Content Area --- */}
      <div className="main-content-grid">

        {/* --- Work Order List (Colorful) --- */}
        <div className="card-container work-order-card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="card-title">Active Work Orders</h3>
            <button onClick={fetchWorkOrders} className="btn btn-sm btn-outline">Refresh</button>
          </div>
          {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading work orders...</div>
          ) : workOrders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No work orders found</div>
          ) : (
            <div className="table-wrapper">
              {/* I created .work-order-table in the CSS for this */}
              <table className="work-order-table">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Task</th>
                    <th>Status</th>
                    <th>Assigned</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <span className={`status-pill ${getPriorityClass(order.priority)}`}>
                        {order.priority}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div className="font-medium">{order.task}</div>
                        {order.eventName && (
                          <div className="text-xs text-gray-500 mt-1">
                            📅 {order.eventName}
                            {order.eventDate && ` • ${new Date(order.eventDate).toLocaleDateString()}`}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-tag ${getStatusClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={order.assignedTo && order.assignedTo !== 'Unassigned' ? order.assignedTo : ''}
                        onChange={(e) => {
                          // Update local state immediately for better UX
                          setWorkOrders(workOrders.map(task => 
                            task.id === order.id ? { ...task, assignedTo: e.target.value } : task
                          ));
                        }}
                        onBlur={(e) => {
                          // Update in database when user leaves the field
                          const newValue = e.target.value.trim();
                          const currentValue = order.assignedTo && order.assignedTo !== 'Unassigned' ? order.assignedTo : '';
                          // Only update if value actually changed
                          if (newValue !== currentValue) {
                            handleUpdateAssigned(order.id, newValue || null);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Update on Enter key
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                        className="form-input"
                        style={{ 
                          width: '100%', 
                          padding: '0.5rem', 
                          fontSize: '0.875rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          minWidth: '120px'
                        }}
                        placeholder="Unassigned"
                      />
                    </td>
                    <td>
                      <button 
                        onClick={() => handleCompleteTask(order.id)}
                        className="btn-icon btn-complete"
                        title="Mark Complete"
                        disabled={order.status === 'Completed'}
                      >
                        <FaCheckCircle />
                      </button>
                      <button 
                        onClick={() => handleDeleteTask(order.id)}
                        className="btn-icon btn-delete"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* --- "Create New Task" Modal (Pop-up) --- */}
      {/* This uses the .modal-overlay from your CSS */}
      {showModal && (
        <div className="modal-overlay">
          {/* We use .modal-card but add our own .maintenance-modal for custom size */}
          <div className="modal-card maintenance-modal">
            <div className="modal-body">
              <div className="flex-header">
                <h3 className="hero-title" style={{ fontSize: '1.5rem' }}>New Work Order</h3>
                <button onClick={() => setShowModal(false)} className="btn-icon">
                  <FaTimes size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateTask}>
                <div className="form-group">
                  <label htmlFor="eventId">Event (Optional)</label>
                  <select
                    id="eventId"
                    value={newEventId}
                    onChange={(e) => setNewEventId(e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select an event (optional)</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name} - {event.date} {event.time ? `• ${event.time}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="taskName">Task Description</label>
                  <input
                    id="taskName"
                    type="text"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    className="form-input"
                    placeholder="e.g., 'Fix leak in Restroom 3'"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="priority">Priority</label>
                  <select
                    id="priority"
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    className="form-input"
                  >
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="assigned">Assigned To</label>
                  <input
                    id="assigned"
                    type="text"
                    value={newAssigned}
                    onChange={(e) => setNewAssigned(e.target.value)}
                    className="form-input"
                    placeholder="Enter person name (optional)"
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Create Task
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

